import {
  UserManager,
  WebStorageStateStore,
  InMemoryWebStorage,
  type UserManagerSettings,
  type User,
} from 'oidc-client-ts';
import type { AuthProviderProps } from 'react-oidc-context';

import { KC_AUTHORITY, KC_CLIENT_ID } from '@/constants/config';
import { Routes } from '@/constants/routes';

// react-oidc-context wiring.
//
// Token hygiene (NF-07): the `userStore` (where access + refresh tokens land
// after a successful exchange) uses `InMemoryWebStorage`, so tokens NEVER hit
// localStorage / sessionStorage. A page reload re-runs the silent renew via
// the Keycloak SSO session cookie (or falls back to a fresh signin redirect).
//
// The `stateStore` (which holds the short-lived PKCE `code_verifier` +
// `state` between the redirect-to-KC and the callback) uses localStorage.
// It cannot be in-memory — the full-page redirect kills in-memory state, so
// PKCE would fail with "No matching state found in storage". It must NOT be
// sessionStorage either: on Android a home-screen / standalone launch resumes
// the app in a fresh browsing context after the KC redirect round-trip, so the
// sessionStorage written before the redirect is gone and the callback hangs
// forever on the spinner (iOS preserves the web context, hence it only broke
// on Android). localStorage is origin-scoped and survives the round-trip in
// every context. This is acceptable per NF-07: it is not a token, it is a
// single-use nonce that oidc-client-ts deletes after the `/token` exchange and
// that is worthless once consumed. (Tokens still live only in
// InMemoryWebStorage below — they never touch localStorage.)
//
// The audience claim is set server-side by Keycloak's
// `audience-potato-rocket-web` protocol mapper (see
// docker/keycloak/potato-rocket-realm.json); the FE only needs to ask for
// `openid` here (see scope note below).

const userStore = new WebStorageStateStore({ store: new InMemoryWebStorage() });
const stateStore = new WebStorageStateStore({ store: window.localStorage });

const settings: UserManagerSettings = {
  authority: KC_AUTHORITY,
  client_id: KC_CLIENT_ID,
  redirect_uri: `${window.location.origin}${Routes.CALLBACK}`,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  // The seeded realm currently has `clientScopes: []` — only the built-in
  // `openid` scope is recognised. `profile` + `email` would fail with
  // `invalid_scope` until those client scopes are added to the realm export.
  // The token already carries `email`/`given_name`/`family_name`/`phoneNumber`
  // via the explicit protocol mappers on `potato-rocket-web`, so no info is
  // lost by dropping `profile`/`email` here.
  scope: 'openid',
  automaticSilentRenew: true,
  loadUserInfo: false, // we have everything we need in the access token
  userStore,
  stateStore,
};

// Shared UserManager singleton — also imported by the axios interceptor so
// every request can pull the current access token from the same source.
export const userManager = new UserManager(settings);

// Dev-only debugging hook. The tokens live in `InMemoryWebStorage` (NF-07), so
// there is no way to simulate an expired session from the console without a
// handle on the UserManager — `__userManager.removeUser()` is exactly the
// "SSO cookie alive, tokens gone" state a laptop sleep produces. Stripped from
// the production bundle by the `import.meta.env.DEV` guard (Vite constant-folds
// it to `false` and dead-code-eliminates the block). It exposes NO token: the
// same manager the app already uses, nothing serialised anywhere.
if (import.meta.env.DEV) {
  (window as unknown as { __userManager?: UserManager }).__userManager = userManager;
}

// react-oidc-context AuthProvider props.
//
// `onSigninCallback` fires *after* react-oidc-context has successfully
// exchanged the auth code for tokens. We:
//   1) strip the `?code=...&state=...` query string from history so the SPA
//      URL stays clean and a refresh doesn't re-trigger the exchange, AND
//   2) navigate from `/auth/callback` to either the user's pre-login
//      destination (deep-link `returnTo` carried via the OIDC `state`
//      param) or, if absent, the app root.
// Both happen via `history.replaceState` (no full reload, no extra render
// pass) so AuthGate sees `isAuthenticated=true` on its next render at
// the resolved target.
//
// `user?.state` is whatever AuthGate passed to `signinRedirect({state: ...})`.
// We round-trip the original `pathname + search` so a logged-out user
// landing on e.g. `/admin/users?q=foo` ends up back there after KC login.
interface ReturnToState {
  returnTo?: string;
}

const isSafeInternalPath = (path: unknown): path is string =>
  typeof path === 'string' &&
  path.startsWith('/') &&
  // reject protocol-relative // and any scheme-like prefix.
  !path.startsWith('//') &&
  !/^\/[^/]*:/.test(path);

export const oidcConfig: AuthProviderProps = {
  ...settings,
  userManager,
  onSigninCallback: (user) => {
    const onCallbackPath = window.location.pathname === Routes.CALLBACK;
    const state = user?.state as ReturnToState | undefined;
    const returnTo = state?.returnTo;
    let target: string;
    if (isSafeInternalPath(returnTo)) {
      target = returnTo;
    } else if (onCallbackPath) {
      target = Routes.HOME;
    } else {
      target = window.location.pathname;
    }
    window.history.replaceState({}, document.title, target);
    // history.replaceState alone doesn't wake React Router. Fire a popstate
    // so <BrowserRouter> re-derives location and routes us off the bare
    // callback path.
    window.dispatchEvent(new PopStateEvent('popstate'));
  },
};

// Pulled out as a helper for the axios interceptor — synchronous read of the
// current user from the in-memory store. Returns null if nobody is logged in.
export const getCurrentUser = async (): Promise<User | null> => {
  return userManager.getUser();
};
